import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BookOpen, CircleCheck, CircleX, Timer, TriangleAlert, X } from 'lucide-react';
import { abandonRoadCodeSeries, answerRoadCodeQuestion } from '../../api/roadCode';
import { Button, ProgressBar, cx, useFocusTrap } from '../../components/charte';
import {
  apiMessage,
  apiStatus,
  lettersLabel,
  lettersOf,
  nextPosition,
  seriesTitle,
  toggleLetter,
  withAnswer,
  type ContentSource,
  type Letter,
  type QuestionView,
  type RoadCodeSeries,
} from '../../lib/roadCode';
import { SourcesLine } from './SourcesLine';
import { LetterChip, QuestionCard } from './QuestionCard';
import { usePrefetchMedia } from './RoadCodeMediaView';

/**
 * Plein écran au-dessus de l'espace élève (barre de navigation masquée), centré sur ordinateur :
 * focus clavier gardé dedans, page du dessous immobile, Échap ferme.
 */
export function FullScreen({ label, onEscape, children }: { label: string; onEscape?: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(true, ref, onEscape);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      className="fixed inset-0 z-[60] flex flex-col bg-white outline-none sm:bg-surface-light"
    >
      {children}
    </div>
  );
}

/** Compte à rebours de l'examen blanc : 20 secondes par question, en rouge sur les 5 dernières. */
function Countdown({ remaining, total }: { remaining: number; total: number }) {
  const urgent = remaining <= 5;
  return (
    <span
      role="timer"
      aria-label={`${remaining} secondes restantes`}
      className={cx(
        'inline-flex h-8 min-w-[64px] items-center justify-center gap-1 rounded-pill px-2.5 text-sm font-semibold tabular-nums transition-colors',
        urgent ? 'bg-danger-soft text-danger-dark' : 'bg-primary-50 text-primary-700',
      )}
    >
      <Timer size={15} aria-hidden />
      {remaining}s
      <span className="sr-only"> sur {total}</span>
    </span>
  );
}

/**
 * Lecteur d'une série, mobile d'abord : une question par écran, illustration en tête, propositions à
 * toucher et boîtier A B C D sous le pouce, bouton « Valider » toujours au même endroit. Correction
 * après chaque question (sauf examen blanc et séries « correction à la fin ») ; à l'examen blanc,
 * 20 secondes par question, la réponse part seule à la fin du temps. Clavier : A à D, Entrée.
 * Chaque réponse est enregistrée aussitôt : quitter puis revenir reprend à la question suivante.
 */
export function SeriesPlayer({
  series,
  themes,
  onUpdate,
  onRefresh,
  onClose,
  onFinished,
  onOpenLesson,
}: {
  series: RoadCodeSeries;
  themes: Array<{ key: string; label: string }>;
  onUpdate: (series: RoadCodeSeries) => void;
  onRefresh: () => void;
  onClose: () => void;
  onFinished: () => void;
  onOpenLesson: (lessonId: string) => void;
}) {
  const [selected, setSelected] = useState<Letter[]>([]);
  /** Question corrigée montrée avant de passer à la suivante (correction au fil de l'eau). */
  const [revealed, setRevealed] = useState<number | null>(null);
  const position = revealed ?? nextPosition(series);
  const entry = position === null ? null : series.questions[position];
  const question = entry && !('missing' in entry && entry.missing) ? (entry as QuestionView) : null;
  const shownAt = useRef(performance.now());
  const selectedRef = useRef<Letter[]>([]);
  selectedRef.current = selected;
  const sentFor = useRef<number | null>(null);

  // Nouvelle question : sélection vide, chrono remis à zéro.
  useEffect(() => {
    if (revealed !== null) return;
    setSelected([]);
    shownAt.current = performance.now();
    sentFor.current = null;
  }, [position, revealed]);

  // Illustration de la question suivante préchargée.
  const upcoming = position === null ? null : series.questions[position + 1];
  usePrefetchMedia(upcoming && !('missing' in upcoming && upcoming.missing) ? (upcoming as QuestionView).media : null);

  const submit = useMutation({
    mutationFn: (letters: Letter[]) =>
      answerRoadCodeQuestion(series.id, {
        position: position!,
        selected: letters,
        elapsedMs: Math.round(performance.now() - shownAt.current),
      }),
    onSuccess: (reply) => {
      onUpdate(withAnswer(series, reply));
      if (series.feedback === 'each') setRevealed(reply.position);
      else if (reply.finished) onFinished();
    },
    onError: (error) => {
      // Réponse déjà enregistrée (autre appareil, double envoi) : la série relue fait foi.
      if (apiStatus(error) === 409) onRefresh();
    },
  });

  const send = useCallback(
    (letters: Letter[]) => {
      if (position === null || submit.isPending || sentFor.current === position) return;
      sentFor.current = position;
      submit.mutate(letters, { onError: () => (sentFor.current = null) });
    },
    [position, submit],
  );

  // Examen blanc : compte à rebours, réponse envoyée seule à la fin du temps.
  const seconds = series.secondsPerQuestion;
  const [remaining, setRemaining] = useState(seconds ?? 0);
  useEffect(() => {
    if (!seconds || position === null || revealed !== null || !question) return;
    const deadline = Date.now() + seconds * 1000;
    setRemaining(seconds);
    const timer = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) {
        window.clearInterval(timer);
        send(selectedRef.current);
      }
    }, 200);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, seconds, revealed, question?.id]);

  const toggle = useCallback(
    (letter: Letter) => {
      if (!question || revealed !== null || submit.isPending) return;
      setSelected((current) => toggleLetter(current, letter, question.multiple));
    },
    [question, revealed, submit.isPending],
  );

  const finishedNow = series.status === 'finished';
  // Série déjà terminée à l'ouverture (finie sur un autre appareil) : directement le résultat.
  useEffect(() => {
    if (series.status === 'finished') onFinished();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const next = () => {
    if (finishedNow) onFinished();
    else setRevealed(null);
  };

  // Clavier (ordinateur) : A à D cochent, Entrée valide ou passe à la suite.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const key = event.key.toUpperCase();
      if (question && ['A', 'B', 'C', 'D'].includes(key) && lettersOf(question).includes(key as Letter)) {
        event.preventDefault();
        toggle(key as Letter);
      } else if (event.key === 'Enter' && !(target instanceof HTMLButtonElement)) {
        event.preventDefault();
        if (revealed !== null) next();
        else if (selectedRef.current.length) send(selectedRef.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const abandon = useMutation({ mutationFn: () => abandonRoadCodeSeries(series.id), onSuccess: onClose });

  const answered = series.questions.filter((q) => !('missing' in q && q.missing) && (q as QuestionView).answer).length;
  const shown = (position ?? series.total - 1) + 1;
  const reply = question?.answer ?? null;
  const corrected = revealed !== null && reply?.expected !== undefined;
  const title = seriesTitle(series.mode, series.theme, themes);

  return (
    <FullScreen label={title} onEscape={onClose}>
      {/* En-tête : quitter, avancement, chrono de l'examen. */}
      <header className="shrink-0 border-b border-line bg-white safe-top">
        <div className="mx-auto flex h-14 max-w-2xl items-center gap-3 px-3">
          <Button variant="ghost" iconOnly icon={X} aria-label="Quitter la série (vos réponses sont gardées)" onClick={onClose} />
          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-[12px] font-medium text-secondary">{title}</p>
            <h1 className="text-[15px] font-semibold text-heading">
              Question {Math.min(shown, series.total)}
              <span className="font-normal text-secondary"> / {series.total}</span>
            </h1>
          </div>
          {seconds && question && revealed === null ? <Countdown remaining={remaining} total={seconds} /> : <span className="w-10" aria-hidden />}
        </div>
        <ProgressBar value={(answered / series.total) * 100} label={`${answered} questions répondues sur ${series.total}`} />
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-2xl pb-6 sm:px-4 sm:pt-5">
          {question && series.status !== 'abandoned' && (
            <QuestionCard
              key={question.id}
              question={question}
              selected={revealed !== null && reply ? reply.selected : selected}
              expected={corrected ? reply!.expected : undefined}
              onToggle={toggle}
              locked={revealed !== null || submit.isPending}
            />
          )}
          {series.status === 'abandoned' && (
            <div className="m-4 rounded-card bg-surface-indigo p-5 text-sm text-primary-900">
              <p className="font-semibold">Cette série a été remplacée par une nouvelle.</p>
              <p className="mt-1">Une seule série reste en cours à la fois : retrouvez la plus récente depuis l’accueil du code.</p>
              <Button className="mt-3" size="sm" onClick={onClose}>
                Retour à l’accueil du code
              </Button>
            </div>
          )}
          {series.status !== 'abandoned' && entry && !question && (
            <div className="m-4 rounded-card bg-warn-soft p-5 text-sm text-warn-fg">
              <p className="flex items-center gap-2 font-semibold">
                <TriangleAlert size={18} aria-hidden /> Cette question n’est plus disponible.
              </p>
              <p className="mt-1">Elle a été retirée de la banque depuis le début de la série. Commencez une nouvelle série.</p>
              <Button className="mt-3" size="sm" loading={abandon.isPending} onClick={() => abandon.mutate()}>
                Quitter la série
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Boîtier sous le pouce : lettres, puis « Valider » ; après correction, l'explication et la suite. */}
      {question && series.status !== 'abandoned' && (
        <footer className="shrink-0 border-t border-line bg-white pb-[max(12px,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.12)]">
          <div className="mx-auto max-w-2xl px-4 pt-3">
            {revealed !== null && reply ? (
              <Feedback
                correct={reply.correct}
                expected={reply.expected}
                explanation={reply.explanation}
                sources={reply.sources}
                lesson={reply.lesson ?? null}
                onOpenLesson={onOpenLesson}
                last={finishedNow}
                onNext={next}
              />
            ) : (
              <>
                <p className="mb-2 text-center text-[12.5px] font-semibold text-primary-700">
                  {question.multiple ? 'Plusieurs réponses' : 'Une seule réponse'}
                </p>
                {/* Boîtier sur une ligne, comme la tablette de l'examen. */}
                <div className="flex gap-2" role="group" aria-label="Boîtier de réponse">
                  {lettersOf(question).map((letter) => {
                    const on = selected.includes(letter);
                    return (
                      <button
                        key={letter}
                        type="button"
                        aria-pressed={on}
                        aria-label={`Réponse ${letter}`}
                        disabled={submit.isPending}
                        onClick={() => toggle(letter)}
                        className={cx(
                          'flex h-12 flex-1 items-center justify-center rounded-row border transition',
                          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100',
                          on ? 'border-primary bg-surface-indigo' : 'border-line bg-surface-light hover:border-primary-200',
                        )}
                      >
                        <LetterChip letter={letter} tone={on ? 'selected' : 'idle'} />
                      </button>
                    );
                  })}
                </div>
                {submit.isError && apiStatus(submit.error) !== 409 && (
                  <p role="alert" className="mt-2 text-center text-[13px] text-danger-dark">
                    {apiMessage(submit.error, 'Réponse non enregistrée : vérifiez votre connexion et réessayez.')}
                  </p>
                )}
                <Button
                  variant="primary"
                  className="mt-3 h-12 w-full text-[15px]"
                  disabled={!selected.length}
                  loading={submit.isPending}
                  onClick={() => send(selected)}
                >
                  Valider ({Math.min(shown, series.total)}/{series.total})
                </Button>
              </>
            )}
          </div>
        </footer>
      )}
    </FullScreen>
  );
}

/** Correction de la question : verdict, bonne réponse, explication, fiche à relire, suite. */
function Feedback({
  correct,
  expected,
  explanation,
  sources,
  lesson,
  onOpenLesson,
  last,
  onNext,
}: {
  correct?: boolean;
  expected?: Letter[];
  explanation?: string;
  sources?: ContentSource[];
  lesson: string | null;
  onOpenLesson: (id: string) => void;
  last: boolean;
  onNext: () => void;
}) {
  return (
    <div aria-live="polite">
      <div className={cx('flex items-start gap-2.5 rounded-row px-3.5 py-3', correct ? 'bg-success-light text-success-dark' : 'bg-danger-light text-danger-dark')}>
        {correct ? <CircleCheck size={20} className="mt-px shrink-0" aria-hidden /> : <CircleX size={20} className="mt-px shrink-0" aria-hidden />}
        <div className="min-w-0 text-[13.5px] leading-normal">
          <p className="text-[15px] font-semibold">{correct ? 'Bonne réponse !' : 'Mauvaise réponse'}</p>
          {!correct && expected && <p className="font-medium">Réponse attendue : {lettersLabel(expected)}</p>}
          {explanation && (
            <div className="mt-1 max-h-[22dvh] overflow-y-auto">
              <p className="text-body">{explanation}</p>
              <SourcesLine sources={sources} className="mt-1" />
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {lesson && !correct && (
          <Button icon={BookOpen} className="h-12 shrink-0" onClick={() => onOpenLesson(lesson)}>
            Revoir la fiche
          </Button>
        )}
        <Button variant="primary" className="h-12 flex-1 text-[15px]" onClick={onNext} data-autofocus>
          {last ? 'Voir mon résultat' : 'Question suivante'}
        </Button>
      </div>
    </div>
  );
}
